import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import AuthDrawer from '../components/AuthDrawer';

const AuthDrawerContext = createContext(null);

/**
 * openAuth('login' | 'register', { onSuccess }) slides the auth drawer in from
 * the right over the current page. With onSuccess the visitor stays where they
 * are (e.g. mid-upload); without it they go where the /login page would send
 * them. Must sit inside the Router and AuthProvider.
 */
export const AuthDrawerProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const opener = useRef(null);

  const openAuth = useCallback((mode = 'login', options = {}) => {
    if (isAuthenticated) {
      if (options.onSuccess) options.onSuccess(); else navigate('/account');
      return;
    }
    opener.current = document.activeElement;
    setRequest({ id: Date.now(), mode, onSuccess: options.onSuccess });
  }, [isAuthenticated, navigate]);

  const closeAuth = useCallback(() => {
    setRequest(null);
    opener.current?.focus?.();
  }, []);

  const value = useMemo(() => ({ openAuth, closeAuth }), [openAuth, closeAuth]);

  return (
    <AuthDrawerContext.Provider value={value}>
      {children}
      <AuthDrawer request={request} onClose={closeAuth} />
    </AuthDrawerContext.Provider>
  );
};

// Outside the provider (isolated renders) fall back to the full page.
const FALLBACK = {
  openAuth: (mode) => window.location.assign(mode === 'register' ? '/register' : '/login'),
  closeAuth: () => {},
};

export const useAuthDrawer = () => useContext(AuthDrawerContext) || FALLBACK;
